import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import DashboardPage from "@/pages/Dashboard";
import NewLoanPage from "@/pages/NewLoan";
import LoanDetailPage from "@/pages/LoanDetail";
import ConsentPage from "@/pages/Consent";
import ReportsPage from "@/pages/Reports";
import AboutPage from "@/pages/About";
import TermsPage from "@/pages/Terms";
import OnboardingPage from "@/pages/Onboarding";
import ProfilePage from "@/pages/Profile";
import SupportPage from "@/pages/Support";

function ProtectedRoute({ children, requireIdentity = true }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">
        <div className="animate-pulse text-sm tracking-[0.3em] uppercase">Loading LeLe</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireIdentity && !user.identity_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function RedirectIfAuthed({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors theme="dark" position="top-center" />
        <Routes>
          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <LoginPage />
              </RedirectIfAuthed>
            }
          />
          <Route
            path="/register"
            element={
              <RedirectIfAuthed>
                <RegisterPage />
              </RedirectIfAuthed>
            }
          />
          <Route path="/consent/:token" element={<ConsentPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireIdentity={false}>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/new"
            element={
              <ProtectedRoute>
                <NewLoanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/:id"
            element={
              <ProtectedRoute>
                <LoanDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
